import React, { useState, useMemo } from 'react';
import { Layout, Typography, Card, Row, Col, Button, Select, Input } from 'antd';
import { FaDonate, FaHeart } from 'react-icons/fa';
import { SearchOutlined } from '@ant-design/icons';

const { Content } = Layout;
const { Title, Paragraph } = Typography;
const { Option } = Select;

const donationOptions = [
  { id: 1, title: "Support a Student", description: "Help provide AI & Tech education to a student.", amount: "$10", impact: "Educates 1 student" },
  { id: 2, title: "Empower Learning", description: "Contribute to mentorship and course development.", amount: "$25", impact: "Supports 3 students" },
  { id: 3, title: "Fund a Scholarship", description: "Sponsor a full AI/Data Science scholarship.", amount: "$100", impact: "Funds 1 full scholarship" },
  { id: 4, title: "JectHub Growth", description: "Support platform development and expansion.", amount: "$250", impact: "Empowers 10+ students" },
];

const DonatePage = () => {
  const [selectedAmount, setSelectedAmount] = useState("All");
  const [searchQuery, setSearchQuery] = useState('');

  const handleAmountChange = (value) => {
    setSelectedAmount(value);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value.trim());
  };

  const filteredDonations = useMemo(() => {
    let filtered = [...donationOptions];
    if (selectedAmount !== "All") {
      filtered = filtered.filter((donation) => donation.amount === selectedAmount);
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (donation) =>
          donation.title.toLowerCase().includes(query) ||
          donation.description.toLowerCase().includes(query)
      );
    }
    return filtered;
  }, [selectedAmount, searchQuery]);

  const handleDonate = (donation) => {
    console.log(`Donating to: ${donation.title} - ${donation.amount}`);
    // Example: window.location.href = `/payment?amount=${donation.amount}&title=${donation.title}`;
  };

  return (
    <Layout>
      <Content style={{ padding: '60px', background: 'linear-gradient(135deg, #f9f0ff, #fffbe6)', minHeight: '100vh' }}>
        <Title level={2} style={{ fontSize: '20px', display: 'flex', alignItems: 'center', color: '#722ed1', marginBottom: '28px', fontWeight: '700' }}>
          <FaDonate style={{ marginRight: '14px', fontSize: '20px', color: '#faad14' }} aria-hidden="true" /> Support JectHub
        </Title>
        <Paragraph style={{ fontSize: '16px', color: '#595959', marginBottom: '32px', maxWidth: '800px' }}>
          Your donation fuels AI, Cybersecurity, and Data Science education for students across Africa. Make a lasting impact today!
        </Paragraph>
        <div style={{ marginBottom: '32px', display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
          <Input
            placeholder="Search donation options"
            prefix={<SearchOutlined />}
            style={{ width: '340px', borderRadius: '8px', height: '44px', boxShadow: '0 3px 6px rgba(0,0,0,0.1)', border: 'none' }}
            onChange={handleSearchChange}
            value={searchQuery}
            aria-label="Search donation options"
          />
          <Select
            defaultValue="All"
            style={{ width: '240px', borderRadius: '8px' }}
            onChange={handleAmountChange}
            value={selectedAmount}
            aria-label="Filter by donation amount"
            dropdownStyle={{ borderRadius: '8px' }}
          >
            <Option value="All">All Amounts</Option>
            {donationOptions.map((option) => (
              <Option key={option.id} value={option.amount}>
                {option.amount}
              </Option>
            ))}
          </Select>
        </div>
        <Row gutter={[24, 24]}>
          {filteredDonations.length > 0 ? (
            filteredDonations.map((donation) => (
              <Col xs={24} sm={12} md={8} lg={6} key={donation.id}>
                <Card
                  hoverable
                  style={{
                    borderRadius: '16px',
                    overflow: 'hidden',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
                    background: 'linear-gradient(135deg, #ffffff, #faf5ff)',
                    height: '320px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    padding: '20px',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease, background 0.3s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-8px)';
                    e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.2)';
                    e.currentTarget.style.background = 'linear-gradient(135deg, #faf5ff, #fffbe6)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
                    e.currentTarget.style.background = 'linear-gradient(135deg, #ffffff, #faf5ff)';
                  }}
                >
                  <div>
                    <Title level={4} style={{ marginBottom: '12px', color: '#1a1a1a', fontSize: '20px', fontWeight: '600' }}>
                      {donation.title}
                    </Title>
                    <Paragraph
                      style={{ color: '#595959', fontSize: '14px', marginBottom: '16px', lineHeight: '1.5' }}
                      ellipsis={{ rows: 2, expandable: false }}
                    >
                      {donation.description}
                    </Paragraph>
                    <Paragraph style={{ marginBottom: '8px', color: '#595959', fontSize: '14px' }}>
                      <strong style={{ color: '#722ed1' }}>Amount:</strong> {donation.amount}
                    </Paragraph>
                    <Paragraph style={{ marginBottom: '0', color: '#888', fontSize: '13px' }}>
                      <strong style={{ color: '#722ed1' }}>Impact:</strong> {donation.impact}
                    </Paragraph>
                  </div>
                  <Button
                    type="primary"
                    icon={<FaHeart style={{ marginRight: '8px' }} />}
                    style={{
                      background: 'linear-gradient(45deg, #faad14, #d48806)',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: 'bold',
                      height: '44px',
                      width: '100%',
                      marginTop: '20px',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'linear-gradient(45deg, #d48806, #ad6800)';
                      e.target.style.boxShadow = '0 6px 12px rgba(0,0,0,0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'linear-gradient(45deg, #faad14, #d48806)';
                      e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
                    }}
                    onClick={() => handleDonate(donation)}
                    aria-label={`Donate ${donation.amount} to ${donation.title}`}
                  >
                    Donate Now
                  </Button>
                </Card>
              </Col>
            ))
          ) : (
            <Col span={24} style={{ textAlign: 'center', marginTop: '24px' }}>
              <Paragraph style={{ color: '#999', fontSize: '16px' }}>
                No donation options match your search. Try adjusting your filters!
              </Paragraph>
            </Col>
          )}
        </Row>
        {/* Custom CSS */}
        <style jsx global>{`
          .ant-select-selector {
            border: none !important;
            box-shadow: 0 3px 6px rgba(0,0,0,0.1) !important;
            height: 44px !important;
            display: flex !important;
            align-items: center !important;
          }
          .ant-select-selection-placeholder {
            color: #888 !important;
          }
        `}</style>
      </Content>
    </Layout>
  );
};

export default DonatePage;